/* @flow */

import React from 'react';
import styled from '@emotion/styled';
import { connect } from 'react-redux';
import { get, filter } from 'lodash/fp';
import numeral from 'numeral';

import { hover } from 'app/utils/cssUtils';
import t from 'app/utils/translate';
import Tappable from 'app/components/commons/Tappable';
import Icon from 'app/components/commons/Icon';
import NavPrimaryRow from './NavPrimaryRow';
import CampaignButton from './CampaignButton';
import BetWithPlayUp from './BetWithPlayUp';
import { TICKET } from 'app/constants/bonusTypes';

const SocialLink = styled(({ children, ...rest }) => (
  <a rel="noopener noreferrer" target="_blank" {...rest}>
    {children}
  </a>
))(({ theme }) => ({
  color: theme.color.basis2,
  backgroundColor: theme.color.white,
  borderRadius: '50%',
  fontSize: 18,
  width: 30,
  height: 30,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginRight: 5,
  marginLeft: 5,
  padding: 0,
  transition: 'color 0.2s',
  ...hover({
    backgroundColor: theme.color.basis4
  })
}));

const NavPrimary: React.FC<Props> = ({
  blogLink,
  displayComboCalculator,
  displayFantasyInsider,
  displayFriends,
  displayGiftStore,
  displayGlobalLeaderboard,
  displayMyTeams,
  displayTraining,
  displayRapidFire,
  externalLinks = [],
  showPlayUpCampaign,
  facebookLink,
  giftStoreName,
  showBetWithPlayUp,
  campaignButtonTitle,
  onToggleCampaignModal,
  instagramLink,
  isSignedIn,
  markLive,
  twitterLink,
  userTotalBonus,
  ticketsCount
}: Props) => {
  return (
    <NavWrapper>
      <MainNav>
        {showPlayUpCampaign && (
          <CampaignButton icon="icon_menu_gift" onClick={() => onToggleCampaignModal(true)}>
            {campaignButtonTitle}
          </CampaignButton>
        )}
        {showBetWithPlayUp && <BetWithPlayUp />}
        <NavPrimaryRow icon="icon_menu_lobby" url="/lobby">
          {t('Lobby')}
        </NavPrimaryRow>
        {isSignedIn && (
          <NavPrimaryRow icon="icon_menu_my_challenges" url="/my-challenges" isLive={markLive}>
            {t('My Challenges')}
          </NavPrimaryRow>
        )}
        {displayMyTeams && isSignedIn && (
          <NavPrimaryRow icon="icon_menu_my_teams" url="/my-teams" isLive={markLive}>
            {t('My Teams')}
          </NavPrimaryRow>
        )}
        <NavPrimaryRow icon="icon_menu_live_now" url="/live-challenges">
          {t('Live Now')}
        </NavPrimaryRow>
        {displayRapidFire && (
          <NavPrimaryRow icon="icon_menu_rapidfire" url="/rapidfire">
            RapidFire
          </NavPrimaryRow>
        )}
        {displayComboCalculator && isSignedIn && (
          <NavPrimaryRow icon="icon-sport-horse" url="/combo-calculator">
            Combo Calc
          </NavPrimaryRow>
        )}
        {displayFantasyInsider && isSignedIn && (
          <NavPrimaryRow icon="icon_menu_my_teams" url="/fantasy-insider" isFree>
            Fantasy Insider
          </NavPrimaryRow>
        )}
        {isSignedIn && (
          <NavPrimaryRow icon="icon_menu_history" url="/account/history">
            {t('History')}
          </NavPrimaryRow>
        )}
        {isSignedIn && (
          <NavPrimaryRow icon="icon-menu-bonus" url="/account/bonus">
            Bonus{' '}
            <UserBonusAmount id="gift">
              {userTotalBonus !== undefined && `$${numeral(userTotalBonus).format('0,0.00')}`}
              {ticketsCount > 0 && (
                <NotificationDot>
                  <span>{ticketsCount}</span>
                </NotificationDot>
              )}
            </UserBonusAmount>
          </NavPrimaryRow>
        )}
        {displayGiftStore && (
          <NavPrimaryRow icon="icon_menu_gift_store" url="/gift">
            {t(giftStoreName)}
          </NavPrimaryRow>
        )}
        {isSignedIn && displayFriends && (
          <NavPrimaryRow icon="icon_menu_friends" url="/groups">
            {t('Friends')}
          </NavPrimaryRow>
        )}
        {displayGlobalLeaderboard && (
          <NavPrimaryRow icon="icon_menu_ultimate_champion" url="/ultimate-champion">
            {t('Ultimate Champ')}
          </NavPrimaryRow>
        )}
        <NavPrimaryRow icon="icon_menu_how_to_play" url="/how-to-play">
          {t('How To Play')}
        </NavPrimaryRow>
        <NavPrimaryRow
          external
          isNew
          icon="icon_menu_draftstars_coach"
          url="https://www.draftstars.com.au/improve-your-dfs-game-with-draftstars-coach/"
        >
          {t('Draftstars Coach')}
        </NavPrimaryRow>
        <NavPrimaryRow icon="icon_menu_how_to_play" url="/faqs">
          Contest Rules
        </NavPrimaryRow>
        {blogLink && (
          <NavPrimaryRow icon="icon_menu_news" url={blogLink} external>
            {t('News & Tips')}
          </NavPrimaryRow>
        )}
        {displayTraining && (
          <NavPrimaryRow icon="icon_menu_training_ground" url="/training">
            {t('Training Ground')}
          </NavPrimaryRow>
        )}
        {Array.isArray(externalLinks) &&
          externalLinks.length > 0 &&
          externalLinks.map(({ url, label, icon }) => (
            <NavPrimaryRow key={label} icon={icon} url={url} external>
              {t(label)}
            </NavPrimaryRow>
          ))}
      </MainNav>
      <Footer>
        {facebookLink && (
          <Tappable component={SocialLink} href={facebookLink}>
            <Icon type="icon-social-facebook" />
          </Tappable>
        )}
        {instagramLink && (
          <Tappable component={SocialLink} href={instagramLink}>
            <Icon type="icon-social-instagram" />
          </Tappable>
        )}
        {twitterLink && (
          <Tappable component={SocialLink} href={twitterLink}>
            <Icon type="icon-social-twitter" />
          </Tappable>
        )}
      </Footer>
    </NavWrapper>
  );
};

const defaultProps = {
  blogLink: undefined,
  campaignButtonTitle: undefined,
  displayComboCalculator: false,
  displayFantasyInsider: false,
  externalLinks: [],
  facebookLink: undefined,
  instagramLink: undefined,
  markLive: false,
  twitterLink: undefined,
  userTotalBonus: undefined,
  ticketsCount: 0
};

NavPrimary.defaultProps = defaultProps;

function mapStateToProps(state) {
  const credits = get('currentUser.bonusPack.bonuses', state.user) || [];
  const ticketsCount = credits ? filter(credit => credit.type === TICKET)(credits).length : 0;
  return {
    userTotalBonus: get('bonusPack.totalBalance', state.user.currentUser),
    ticketsCount
  };
}

export default React.memo(connect(mapStateToProps)(NavPrimary));

interface Props {
  blogLink?: string;
  displayComboCalculator?: boolean;
  displayFantasyInsider?: boolean;
  displayFriends: boolean;
  displayGiftStore: boolean;
  displayGlobalLeaderboard: boolean;
  displayMyTeams: boolean;
  displayTraining: boolean;
  externalLinks?: Array<*>;
  showPlayUpCampaign: boolean;
  displayRapidFire: boolean;
  giftStoreName: string;
  showBetWithPlayUp: boolean;
  campaignButtonTitle?: string;
  facebookLink?: string;
  instagramLink?: string;
  onToggleCampaignModal: () => void;
  isSignedIn: boolean;
  markLive?: boolean;
  twitterLink?: string;
  userTotalBonus?: number;
  ticketsCount: Number;
}

const UserBonusAmount = styled.span`
  position: relative;
  font-size: 0.8rem;
`;

const NotificationDot = styled.div(({ theme }) => ({
  border: '1px solid',
  borderRadius: '50%',
  position: 'absolute',
  top: -10,
  right: -15,
  height: 17,
  width: 17,
  textAlign: 'center',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.color.primary,
  borderColor: theme.color.primary,
  color: theme.color.white,
  fontSize: '0.7rem',
  fontWeight: theme.fontWeight.bold
}));

const NavWrapper = styled.div({
  overflow: 'hidden',
  paddingTop: 20,
  textTransform: 'uppercase'
});

const MainNav = styled.div();

const Footer = styled.div(({ theme }) => ({
  display: 'flex',
  paddingTop: theme.spacing.default,
  paddingLeft: 8
}));
